import { FC, ReactNode } from 'react';
import styled from 'styled-components';
import tw from 'twin.macro';

import { Checkbox } from '@/components';
import strings from '@/language/Swedish';
import {
  ReferencePriceType,
  SoldRefDataType,
  ProjectType,
} from '@/types/dataTypes';
import {
  formatPrice,
  getAboutListItems,
  getSelectedRefSqmPrice,
} from '@/helpers';

const StyledAbout = styled.div`
  ${tw`flex-1 p-4 space-y-6 bg-white/80 text-xs rounded-2xl border border-slate-200`}
`;

const Header = styled.h2`
  ${tw`text-sm mb-0`}
`;

const Ul = styled.ul`
  ${tw`flex-1 space-y-3`}
`;

const Li = styled.li`
  ${tw`flex items-center gap-3`}
`;

const Title = styled.span`
  ${tw`text-slate-500 shrink-0 w-28`}
`;

interface Props {
  refData: SoldRefDataType;
  children?: ReactNode;
  referencePrice: ReferencePriceType;
  project: ProjectType;
  setShowRefPrice: () => void;
  showRefPrice: boolean;
}

const About: FC<Props> = ({
  refData,
  children,
  referencePrice,
  project,
  setShowRefPrice,
  showRefPrice,
}) => {
  const soldFilteredTable = project.soldTableData?.filter((obj) =>
    project.soldTableIds?.selected?.has(obj.id)
  );

  const soldTable =
    soldFilteredTable?.length === 0 ? project.soldTableData : soldFilteredTable;

  const formattedPrice = referencePrice?.sqmPrice
    ? formatPrice(referencePrice.sqmPrice)
    : 0;

  const calculatedRefPrice =
    referencePrice && refData
      ? formatPrice(
          (referencePrice.sqmPrice || 0) * (refData.living_area_reference || 0)
        )
      : 0;

  const referencePriceString =
    strings.report.referencePrice1 +
    soldTable?.length +
    strings.report.referencePrice2 +
    ` ${calculatedRefPrice}kr (${formattedPrice})kr/m²`;

  const selectedRefSqmPrice =
    project.soldTableData &&
    getSelectedRefSqmPrice(
      project.soldTableData,
      project.soldTableIds.selected
    );

  const calculatedSelectedRefPrice =
    selectedRefSqmPrice && refData
      ? formatPrice(
          (selectedRefSqmPrice || 0) * (refData.living_area_reference || 0)
        )
      : 0;

  const selectedReferencePriceString =
    strings.report.referencePrice1 +
    soldTable?.length +
    strings.report.selectedReferencePrice2 +
    ` ${calculatedSelectedRefPrice}kr (${formatPrice(selectedRefSqmPrice || 0)})kr/m²`;

  const listItems = getAboutListItems(refData);

  return (
    <StyledAbout>
      <Header>{strings.report.about}</Header>
      <Ul>
        {listItems
          .filter((item) => item.value)
          .map(({ title, value }) => (
            <Li key={title}>
              <Title>{title}:</Title>
              <span>{value}</span>
            </Li>
          ))}
      </Ul>
      <Ul>
        <li>
          <Checkbox
            id={'add_ref_price_total_to_pdf'}
            label={
              soldFilteredTable?.length === 0
                ? referencePriceString
                : selectedReferencePriceString
            }
            isChecked={showRefPrice}
            onToggle={() => setShowRefPrice()}
          />
        </li>
      </Ul>
      {children}
    </StyledAbout>
  );
};

export default About;
